// Text blocks: heading, rich text, buttons, separator, spacer, icon and raw HTML.
import { registerBlock } from '../core/registry.js';
import { classList, replaceClasses, findClass } from '../core/dom.js';
import { safeUrl } from '../core/sanitize.js';
import { readStyle, writeStyle } from '../core/styles.js';
import { contentIcons } from '../ui/icons.js';
import { TEXTUAL_TAGS, INLINE_TAGS } from './structure.js';

const HEADING_SIZE_CLASS = /^(?:display-[1-6]|h[1-6]|fs-[1-6])$/;

/** True when every child of a div is text content: the div is then a text block with its own classes. */
export function isTextualDiv(el) {
    if (el.localName !== 'div' || !el.childNodes.length) return false;
    const reserved = classList(el).some(name => name === 'row' || name.startsWith('container') || name.startsWith('pflow-') || /^col(?:-|$)/.test(name));
    return !reserved && hasTextualChildren(el);
}

/** True when an element holds text content only (paragraphs, lists, inline markup...). */
export function hasTextualChildren(el) {
    let hasContent = false;
    for (const node of el.childNodes) {
        if (node.nodeType === 3) {
            if (node.textContent.trim()) hasContent = true;
            continue;
        }
        if (node.nodeType !== 1) continue;
        const tag = node.localName;
        const textual = TEXTUAL_TAGS.has(tag) || INLINE_TAGS.has(tag) || tag === 'figure' && !classList(node).some(name => name.startsWith('pflow-'));
        if (!textual || node.classList.contains('pflow-heading')) return false;
        hasContent = true;
    }
    return hasContent;
}

registerBlock({
    name: 'heading',
    label: 'block.heading',
    icon: 'text-h',
    category: 'basic',
    priority: 60,
    match: el => /^h[1-6]$/.test(el.localName) && (el.localName === 'h1' || el.classList.contains('pflow-heading')),
    create: ({ h, t }) => h('h2', { class: 'pflow-heading' }, t('placeholder.heading')),
    prepare: el => el.setAttribute('data-pf-edit', 'inline'),
    fields: [
        {
            type: 'segmented',
            label: 'field.level',
            options: ['h1', 'h2', 'h3', 'h4', 'h5', 'h6'].map(value => ({ value, label: value.toUpperCase() })),
            get: el => el.localName,
            set: (el, value, ctx) => ctx.retag(el, value),
        },
        {
            type: 'select',
            label: 'field.size',
            options: [
                { value: '', label: 'option.default' },
                ...['1', '2', '3', '4', '5', '6'].map(n => ({ value: `display-${n}`, label: `Display ${n}` })),
                ...['1', '2', '3', '4', '5', '6'].map(n => ({ value: `h${n}`, label: `H${n}` })),
            ],
            get: el => findClass(el, HEADING_SIZE_CLASS),
            set: (el, value) => replaceClasses(el, HEADING_SIZE_CLASS, value),
            pattern: HEADING_SIZE_CLASS,
        },
    ],
});

registerBlock({
    name: 'text',
    label: 'block.text',
    icon: 'text-align-left',
    category: 'basic',
    priority: 40,
    match: el => (el.localName === 'div' && el.classList.contains('pflow-editor')) || isTextualDiv(el),
    create: ({ h, t }) => h('div', { class: 'pflow-editor' }, h('p', {}, t('placeholder.text'))),
    prepare: el => el.setAttribute('data-pf-edit', 'rich'),
    presets: [
        {
            id: 'quote',
            label: 'block.quote',
            icon: 'quotes',
            html: '<div class="pflow-editor"><figure><blockquote class="blockquote"><p>{placeholder.quote}</p></blockquote><figcaption class="blockquote-footer">{placeholder.author}</figcaption></figure></div>',
        },
        {
            id: 'list',
            label: 'block.list',
            icon: 'list-bullets',
            html: '<div class="pflow-editor"><ul><li>{placeholder.item} 1</li><li>{placeholder.item} 2</li><li>{placeholder.item} 3</li></ul></div>',
        },
        {
            id: 'table',
            label: 'block.table',
            icon: 'table',
            category: 'components',
            html: '<div class="pflow-editor"><table class="table"><thead><tr><th>{placeholder.column} 1</th><th>{placeholder.column} 2</th><th>{placeholder.column} 3</th></tr></thead><tbody><tr><td>—</td><td>—</td><td>—</td></tr><tr><td>—</td><td>—</td><td>—</td></tr></tbody></table></div>',
        },
    ],
});

// ---- Buttons ---------------------------------------------------------------------------------------

const BUTTON_STYLE_CLASS = /^btn-(?:outline-)?(?:primary|secondary|success|danger|warning|info|light|dark|link)$/;
const BUTTON_SIZE_CLASS = /^btn-(?:sm|lg)$/;

export const BUTTON_STYLES = ['primary', 'secondary', 'success', 'danger', 'warning', 'info', 'light', 'dark']
    .flatMap(color => [`btn-${color}`, `btn-outline-${color}`])
    .concat(['btn-link']);

export function buttonStyle(a) {
    return findClass(a, BUTTON_STYLE_CLASS);
}

registerBlock({
    name: 'button',
    label: 'block.button',
    icon: 'cursor-click',
    category: 'basic',
    priority: 60,
    match: el => el.classList.contains('pflow-button'),
    create: ({ h, t }) => h('div', { class: 'pflow-button' }, h('a', { href: '#', class: 'btn btn-primary' }, t('placeholder.button'))),
    prepare: el => el.querySelectorAll('a').forEach(a => a.setAttribute('data-pf-edit', 'plain')),
    fields: [{ type: 'buttons' }],
});

/** Sets the link of an anchor from a user value (unsafe URLs are refused). */
export function setLink(a, value, newTab) {
    const url = safeUrl(value);
    if (url) a.setAttribute('href', url);
    else a.setAttribute('href', '#');
    if (newTab !== undefined) {
        if (newTab) {
            a.setAttribute('target', '_blank');
            a.setAttribute('rel', 'noopener');
        } else {
            a.removeAttribute('target');
            if (a.getAttribute('rel') === 'noopener') a.removeAttribute('rel');
        }
    }
}

// ---- Separator and spacer --------------------------------------------------------------------------

const HR_WIDTH_CLASS = /^w-(?:25|50|75|100)$/;
const OPACITY_CLASS = /^opacity-(?:10|25|50|75|100)$/;

registerBlock({
    name: 'separator',
    label: 'block.separator',
    icon: 'minus',
    category: 'basic',
    priority: 60,
    match: el => el.localName === 'hr' || (el.localName === 'div' && el.classList.contains('pflow-hr')),
    create: ({ h }) => h('hr'),
    // The editor wraps the <hr> so that it can be clicked; the wrapper never reaches the export.
    wrap: true,
    fields: [
        {
            type: 'select',
            label: 'field.width',
            options: [{ value: '', label: '100%' }, { value: 'w-75 mx-auto', label: '75%' }, { value: 'w-50 mx-auto', label: '50%' }, { value: 'w-25 mx-auto', label: '25%' }],
            target: el => el.querySelector('hr'),
            get: el => {
                const width = findClass(el, HR_WIDTH_CLASS);
                return width && width !== 'w-100' ? `${width} mx-auto` : '';
            },
            set: (el, value) => {
                replaceClasses(el, /^(?:w-(?:25|50|75|100)|mx-auto)$/, value);
            },
            pattern: /^(?:w-(?:25|50|75|100)|mx-auto)$/,
        },
        {
            type: 'range',
            label: 'field.thickness',
            min: 1,
            max: 12,
            unit: 'px',
            target: el => el.querySelector('hr'),
            get: el => parseInt(readStyle(el, 'border-top-width'), 10) || 1,
            set: (el, value) => writeStyle(el, 'border-top-width', parseInt(value, 10) > 1 ? `${parseInt(value, 10)}px` : ''),
        },
        {
            type: 'select',
            label: 'field.opacity',
            options: [{ value: '', label: 'option.default' }, ...['10', '25', '50', '75', '100'].map(value => ({ value: `opacity-${value}`, label: `${value}%` }))],
            target: el => el.querySelector('hr'),
            get: el => findClass(el, OPACITY_CLASS),
            set: (el, value) => replaceClasses(el, OPACITY_CLASS, value),
            pattern: OPACITY_CLASS,
        },
    ],
});

registerBlock({
    name: 'spacer',
    label: 'block.spacer',
    icon: 'arrows-vertical',
    category: 'basic',
    priority: 60,
    match: el => el.classList.contains('pflow-spacer'),
    create: ({ h }) => h('div', { class: 'pflow-spacer', style: 'height: 48px' }),
    fields: [
        {
            type: 'range',
            label: 'field.height',
            min: 8,
            max: 240,
            step: 4,
            unit: 'px',
            get: el => parseInt(readStyle(el, 'height'), 10) || 48,
            set: (el, value) => writeStyle(el, 'height', `${parseInt(value, 10)}px`),
        },
    ],
});

// ---- Icon ------------------------------------------------------------------------------------------

export function iconSvg(name) {
    const markup = contentIcons[name] || contentIcons.star;
    return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 256 256" width="1em" height="1em" fill="currentColor" aria-hidden="true">${markup}</svg>`;
}

registerBlock({
    name: 'icon',
    label: 'block.icon',
    icon: 'star',
    category: 'basic',
    priority: 60,
    match: el => el.classList.contains('pflow-icon'),
    // An icon written by hand with only its name (data-icon) gets its drawing.
    prepare: el => {
        const name = el.getAttribute('data-icon');
        if (name && contentIcons[name] && !el.querySelector('svg')) el.innerHTML = iconSvg(name);
    },
    create: ({ h }) => {
        const el = h('div', { class: 'pflow-icon text-primary', 'data-icon': 'star', style: 'font-size: 48px' });
        el.innerHTML = iconSvg('star');
        return el;
    },
    fields: [
        {
            type: 'icon',
            label: 'field.icon',
            get: el => el.getAttribute('data-icon') || '',
            set: (el, value) => {
                if (!contentIcons[value]) return;
                el.setAttribute('data-icon', value);
                el.innerHTML = iconSvg(value);
            },
        },
        {
            type: 'range',
            label: 'field.size',
            min: 16,
            max: 160,
            step: 4,
            unit: 'px',
            get: el => parseInt(readStyle(el, 'font-size'), 10) || 48,
            set: (el, value) => writeStyle(el, 'font-size', `${parseInt(value, 10)}px`),
        },
    ],
});

// ---- Raw HTML --------------------------------------------------------------------------------------

registerBlock({
    name: 'html',
    label: 'block.html',
    icon: 'code',
    category: 'advanced',
    priority: -100, // the fallback: anything PageFlow does not recognize stays untouched
    match: () => true,
    create: ({ h, t }) => h('div', { class: 'pflow-html' }, h('p', {}, t('placeholder.html'))),
    fields: [{ type: 'html' }],
});
